import { useAuthStore } from '@/stores/authStore'
import {
  canAccessElement,
  canAccessPage,
  resolveAccessRole,
  type AccessRole,
  type ElementPermission,
  type PagePermission,
} from '@/utils/permissions'

export function useAuth() {
  const user = useAuthStore((state) => state.user)
  const token = useAuthStore((state) => state.token)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const login = useAuthStore((state) => state.login)
  const logout = useAuthStore((state) => state.logout)

  const accessRole = resolveAccessRole(user)
  const isSuperAdmin = accessRole === 'superadmin'
  const isAdmin = accessRole === 'admin'
  const isOperator = user?.role === 'operator'
  const isSupervisor = user?.role === 'supervisor'
  const isAgent = user?.role === 'agent' || isSupervisor
  const isViewer = user?.role === 'viewer' || isAgent

  const can = (
    permission:
      | 'manage_users'
      | 'manage_instances'
      | 'manage_webhooks'
      | 'manage_tags'
      | 'manage_tenants'
      | 'finalize_conversations'
      | 'view_all',
  ): boolean => {
    switch (permission) {
      case 'manage_users':
        return isSuperAdmin || isAdmin
      case 'manage_instances':
        return isSuperAdmin || isAdmin
      case 'manage_webhooks':
        return isSuperAdmin
      case 'manage_tags':
        return isSuperAdmin || isAdmin
      case 'manage_tenants':
        return isSuperAdmin
      case 'finalize_conversations':
        return Boolean(user)
      case 'view_all':
        return isSuperAdmin || isAdmin
      default:
        return false
    }
  }

  const canAccessPageScope = (page: PagePermission): boolean => canAccessPage(user, page)
  const canAccessElementScope = (element: ElementPermission): boolean => canAccessElement(user, element)

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    accessRole,
    isSuperAdmin,
    isAdmin,
    isOperator,
    isSupervisor,
    isAgent,
    isViewer,
    can,
    canAccessPage: canAccessPageScope,
    canAccessElement: canAccessElementScope,
    login,
    logout,
  }
}
